import { fetchWrapper } from 'helpers/helpers';
import { ROLES } from 'permission';
import { ROUTES } from 'router/constants';
import {
  setAllCompanies,
  setForeignLanguages,
  setLevels,
  setListHRL,
  setListHrManagers,
  setListLocations,
  setListServices,
} from 'store/reducers/commonReducer';
import type {
  CompanyName,
  ForeignLanguageList,
  LabelValue,
  Office,
  ServiceSimple,
  User,
  Uuid,
} from 'store/reducers/types';
import type { JobWorkflowStep } from 'store/reducers/jobReducer';
import type { AppThunk } from 'store';

export function getListServices(body: Record<string, unknown> = {}): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<ServiceSimple[]>({
      url: 'services/',
      body,
    })
      .then(resp => {
        if (resp) dispatch(setListServices(resp));
      })
      .catch(() => {});
}

export function getListLocations(body: Record<string, unknown> = {}): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<Office[]>({
      url: 'offices/',
      body,
    })
      .then(resp => {
        if (resp) dispatch(setListLocations(resp));
      })
      .catch(() => {});
}

export const getForeignLanguages = (): AppThunk<Promise<void>> => dispatch =>
  fetchWrapper<ForeignLanguageList>({
    url: 'foreign-languages/',
  })
    .then(resp => {
      if (!resp) return;
      dispatch(setForeignLanguages(resp.languages));
      dispatch(setLevels(resp.levels));
    })
    .catch(() => {});

export function getListUsersByRole(body: Record<string, unknown>): Promise<User[] | null | undefined> {
  return fetchWrapper<User[]>({
    method: 'GET',
    url: `users/`,
    body,
  });
}

export function getListHrManagers(
  data?: Record<string, unknown>,
  dashboardExtraHR?: LabelValue
): AppThunk<Promise<LabelValue[] | undefined>> {
  return dispatch => {
    const body = data || { role: ROLES.RECRUITER };
    return fetchWrapper<User[]>({
      method: 'GET',
      url: `users/`,
      body,
    })
      .then(resp => {
        if (!resp) return undefined;
        const list: LabelValue[] = resp.filter(v => !v.is_deleted).map(v => ({ value: v.uuid, label: v.full_name }));
        if (dashboardExtraHR) list.push(dashboardExtraHR);
        dispatch(setListHrManagers(list));
        return list;
      })
      .catch(() => undefined);
  };
}

export function getStepsAssignToJob(id: Uuid): Promise<JobWorkflowStep[] | null | undefined> {
  return fetchWrapper<JobWorkflowStep[]>({
    url: `jobs/${id}/workflow/steps/`,
  })
    .then(resp => resp)
    .catch(() => undefined);
}

export function changeRole(userId: Uuid, data: { role: number | null }): Promise<unknown> {
  return fetchWrapper({
    method: 'PATCH',
    url: `users/${userId}/`,
    body: data,
  }).then(() => {
    window.open(ROUTES.DASHBOARD, '_self');
  });
}

export function getListHRL(): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<User[]>({
      method: 'GET',
      url: `users/`,
      body: { role: ROLES.HR_LEAD },
    }).then(resp => {
      if (resp) dispatch(setListHRL(resp.filter(hrl => !hrl.is_deleted)));
    });
}

export function getAllCompanies(): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<CompanyName[]>({
      method: 'GET',
      url: `companies/`,
    }).then(resp => {
      if (resp) dispatch(setAllCompanies(resp));
    });
}

export function postFeedback(body: Record<string, unknown>): Promise<unknown> {
  return fetchWrapper({
    method: 'POST',
    url: `feedbacks/`,
    version: 'v2',
    body,
  });
}
